/**
 * KANBAN MAPPERS — conversione tra tipi UI e DTO REST.
 *
 * Il contratto REST è AGNOSTICO rispetto all'entità: la board è identificata da `className`
 * (FQN dell'entità gestita) e dalla `pipelineId`. TS-101: nessuna membership persistita — i
 * preventivi della board si ottengono a runtime dal filtro della kanban (il param `kanbanId` sul
 * REST del preventivo). Lo stato/colonna del singolo item è nativo (`Quotation.currentStateId`).
 *
 * Fonte di verità dei types Kanban: `Kanban`, `KanbanDto`, `KanbanInput` vivono qui;
 * `kanbanTypes.ts` li re-esporta.
 */

import { pickAuditFields, type AuditFields } from '@crm/shared';

// ─── Filtro della board (TS-102) ───────────────────────────────────────
//
// Quali preventivi la bacheca mostra, fra quelli della sua pipeline: RESTRINGE
// e non sostituisce. Assente o senza gruppi = nessun restringimento.
// Condizioni in AND dentro il gruppo, gruppi in OR fra loro.
//
// I valori sono SEMPRE stringhe, anche per numeri e date: e' il contratto REST.
// Percio' shape UI e DTO coincidono e i mapper sono passanti — voluto, non una
// svista.

/**
 * Lo scope di una condizione: su quale entita' vive il campo filtrato.
 *
 * NON e' un'unione chiusa, ed e' cambiato con il catalogo contribuibile: gli scope li dichiarano i
 * contributori lato server, quindi un plugin puo' portarne uno che il core non conosce. Elencarli
 * qui vorrebbe dire riscrivere questo file a ogni plugin, e nel frattempo rifiutare a tipo cio' che
 * il server accetta.
 *
 * I valori validi arrivano dal catalogo, `GET /kanbans/filter-fields/{groupId}`: l'editor li
 * prende da li' e non li deriva. Il blocco SYNC che stava qui e' stato TOLTO insieme all'enum
 * Java: una tabella di sincronia che sopravvive alla cosa che sincronizzava e' peggio di nessuna
 * tabella, perche' continua a sembrare vera.
 */
export type KanbanFilterScope = string;

// SYNC with KanbanFilter.Operator (enum Java, ancora chiuso)
// File: modules/crm-core/crm-core-api/src/main/java/it/dvel/crm/core/kanban/KanbanFilter.java
export type KanbanFilterOperator =
  | 'EQ' | 'NEQ' | 'GT' | 'GTE' | 'LT' | 'LTE'
  | 'CONTAINS' | 'NOT_CONTAINS' | 'BETWEEN' | 'IN';

export interface KanbanFilterCondition {
  scope: KanbanFilterScope;
  fieldName: string;
  operator: KanbanFilterOperator;
  values: string[];
}

export interface KanbanFilterGroup {
  conditions: KanbanFilterCondition[];
}

export interface KanbanFilter {
  groups: KanbanFilterGroup[];
}

// Catalogo dei campi filtrabili: GET /kanbans/filter-fields/{groupId}, envelope
// Page. Le etichette arrivano gia' localizzate. Operatori e valori enumerati li
// manda il server, il client non li deriva.
export interface KanbanFilterFieldValue {
  value: string;
  label: string;
}

export interface KanbanFilterFieldOperator {
  code: KanbanFilterOperator;
  label: string;
  /** SINGLE = un valore, RANGE = coppia, SET = insieme. */
  cardinality: string;
}

export interface KanbanFilterField {
  scope: KanbanFilterScope;
  /**
   * Etichetta dell'ambito, GIA' LOCALIZZATA dal server.
   *
   * Il client non la deriva dallo scope: un ambito dichiarato da un plugin non ha un nome che il
   * core sappia tradurre, e costruire una chiave i18n dal valore dello scope funzionava solo
   * finche' gli scope erano tre e li conosceva il core.
   */
  scopeLabel: string;
  fieldName: string;
  label: string;
  // SYNC con KanbanFilterField.Type del backend (crm-core-api).
  type: 'STRING' | 'INTEGER' | 'DECIMAL' | 'MONEY' | 'DATE' | 'ENUM';
  operators: KanbanFilterFieldOperator[];
  enumValues?: KanbanFilterFieldValue[];
}

// ─── DTO REST (shape wire) ─────────────────────────────────────────────

export interface KanbanDto {
  kanbanId: number;
  companyId: number;
  groupId: number;
  userId: number;
  userName: string;
  createDate: string;
  modifiedDate: string;
  name: string;
  /** FQN dell'entità gestita dalla board (una board = un solo tipo). */
  className: string;
  pipelineId: number;
  /** Filtro della board. Assente = nessun restringimento. */
  filter?: KanbanFilter | null;
}

// ─── Shape UI ──────────────────────────────────────────────────────────

export interface Kanban extends AuditFields {
  kanbanId: number;
  name: string;
  /** FQN dell'entità gestita: il client la mappa all'adapter (card + colonne + move). */
  className: string;
  pipelineId: number;
  /** Filtro della board. Assente = nessun restringimento. */
  filter?: KanbanFilter | null;
}

// ─── DTO di input (POST /kanbans/{groupId} e PATCH /kanban/{id}) ───────

export interface KanbanInput {
  name?: string;
  /** Opzionale in creazione: il backend default a Quotation se assente. */
  className?: string;
  pipelineId?: number;
  /**
   * Filtro come oggetto STRUTTURATO: il JSON lo produce il service.
   *
   * ASSENTE significa "non toccarlo" (PATCH parziale); per TOGLIERE il filtro si
   * manda un oggetto SENZA GRUPPI, che e' un'informazione diversa dal silenzio.
   * Mandare null equivarrebbe a non dire niente, quindi non si svuoterebbe mai.
   */
  filter?: KanbanFilter;
}

// ─── Whole-DTO mappers ─────────────────────────────────────────────────

/** DTO REST -> modello UI. Usato su GET e sulle response di POST/PATCH. */
export function kanbanFromDto(dto: KanbanDto): Kanban {
  return {
    kanbanId: dto.kanbanId,
    ...pickAuditFields(dto),
    name: dto.name,
    className: dto.className ?? '',
    pipelineId: dto.pipelineId,
    filter: dto.filter?.groups?.length ? dto.filter : null,
  };
}

/**
 * Modello UI -> DTO REST. Serve ai mock MSW per riprodurre la shape emessa dal backend.
 * L'app reale non lo chiama.
 */
export function kanbanToDto(kanban: Kanban): KanbanDto {
  return {
    kanbanId: kanban.kanbanId,
    ...pickAuditFields(kanban),
    name: kanban.name,
    className: kanban.className,
    pipelineId: kanban.pipelineId,
    filter: kanban.filter?.groups?.length ? kanban.filter : null,
  };
}

/**
 * Payload create UI -> DTO input. Il tipo (className) **non si invia**, e dal TS-117 la ragione è
 * cambiata: non c'è più un default a Quotation, il backend lo **deriva dalla pipeline**. Mandarlo
 * sarebbe legittimo ma inutile — verrebbe solo verificato, e se non corrispondesse darebbe 400.
 *
 * TS-101: nessuna selezione di membri (la board mostra per filtro i record della sua pipeline).
 */
export function kanbanCreateToInput(p: {
  name: string;
  pipelineId: number;
  filter?: KanbanFilter | null;
}): KanbanInput {
  return {
    name: p.name,
    pipelineId: p.pipelineId,
    filter: p.filter ?? EMPTY_FILTER,
  };
}

/** "Nessun filtro" in SCRITTURA: un oggetto senza gruppi, non l'assenza. */
const EMPTY_FILTER: KanbanFilter = { groups: [] };

/**
 * Payload update UI -> DTO input parziale (PATCH): nome e filtro.
 *
 * `filter: null` in ingresso significa "togli il filtro", e sul filo diventa un
 * oggetto senza gruppi. Il test e' su `!== undefined` e non sulla verita' del
 * valore: altrimenti svuotare un filtro sarebbe indistinguibile dal non toccarlo.
 */
export function kanbanUpdateToInput(p: {
  name?: string;
  filter?: KanbanFilter | null;
}): KanbanInput {
  const out: KanbanInput = {};
  if (p.name !== undefined) out.name = p.name;
  if (p.filter !== undefined) out.filter = p.filter ?? EMPTY_FILTER;
  return out;
}

/** DTO input (PATCH) -> `Partial<Kanban>` UI. Usato dai mock per applicare la patch. */
export function kanbanInputFromDto(input: KanbanInput): Partial<Kanban> {
  const out: Partial<Kanban> = {};
  if (input.name !== undefined) out.name = input.name;
  // Stessa semantica del backend, o il mock mentirebbe: filtro assente = non
  // toccare, oggetto presente = sostituisci, senza gruppi = togli.
  if (input.filter !== undefined) out.filter = input.filter.groups.length ? input.filter : null;
  return out;
}
